import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  ArrowLeft,
  FolderOpen,
  User,
  Calendar,
  DollarSign,
  Users,
  Info,
  UserCog, // 🆕 NEW: Icon for project managers
} from "lucide-react";
import { Textarea } from "../components/ui/textarea";
import axios from "axios";
import { Spinner } from "../components/ui/shadcn-io/spinner";
import SimplePaymentStructure, {
  PaymentStructure,
  PaymentMilestone, // 🆕 NEW: Import PaymentMilestone type
} from "../components/simplePaymentStructure";

interface FormData {
  clientId: string;
  projectName: string;
  projectDescription: string;
  startDate: string;
  estimatedEndDate: string;
  totalAmount: string;
  priority: string;
  notes: string;
}

interface Client {
  _id: string;
  clientName: string;
  contactPerson: {
    name: string;
    email: string;
  };
}

interface Employee {
  _id: string;
  fullName: string;
  email: string;
  employeeType: "Employee" | "Intern" | "Part-Time";
  designation?: string;
  isActive: boolean;
  resignationDate?: string;
}

export default function AddProject() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isEditMode = Boolean(id);
  const preSelectedClientId = searchParams.get("clientId");

  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingData, setIsFetchingData] = useState(false);
  const [error, setError] = useState("");
  const [clients, setClients] = useState<Client[]>([]);
  const [paymentStructure, setPaymentStructure] =
    useState<PaymentStructure | null>(null);

  const [formData, setFormData] = useState<FormData>({
    clientId: preSelectedClientId || "",
    projectName: "",
    projectDescription: "",
    startDate: new Date().toISOString().split("T")[0],
    estimatedEndDate: "",
    totalAmount: "",
    priority: "medium",
    notes: "",
  });

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [activeEmployees, setActiveEmployees] = useState<Employee[]>([]);
  const [selectedProjectManagers, setSelectedProjectManagers] = useState<
    string[]
  >([]); // 🆕 NEW
  const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
  const [isFetchingEmployees, setIsFetchingEmployees] = useState(false);

  // Fetch clients and project data
  useEffect(() => {
    fetchClients();
    if (isEditMode && id) {
      fetchProjectData(id);
    }
  }, [isEditMode, id]);

  const fetchClients = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}api/clients`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setClients(response.data.data || []);
    } catch (error: any) {
      console.error("Error fetching clients:", error);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      setIsFetchingEmployees(true);
      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}api/employees`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const allEmployees = response.data || [];
      setEmployees(allEmployees);

      // Filter to show only active employees
      const activeOnly = allEmployees.filter((emp: Employee) => emp.isActive);
      setActiveEmployees(activeOnly);
    } catch (error: any) {
      console.error("Error fetching employees:", error);
    } finally {
      setIsFetchingEmployees(false);
    }
  };

  // 🆕 NEW: Handle project manager selection
  const handleProjectManagerSelection = (managerId: string) => {
    setSelectedProjectManagers((prev) => {
      if (prev.includes(managerId)) {
        return prev.filter((id) => id !== managerId);
      } else {
        return [...prev, managerId];
      }
    });
  };

  const handleEmployeeSelection = (employeeId: string) => {
    setSelectedEmployees((prev) => {
      if (prev.includes(employeeId)) {
        return prev.filter((id) => id !== employeeId);
      } else {
        return [...prev, employeeId];
      }
    });
  };

  const fetchProjectData = async (projectId: string) => {
    try {
      setIsFetchingData(true);
      setError("");

      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}api/projects/${projectId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const project = response.data.data.project;

      // Fill form with existing data
      setFormData({
        clientId: project.clientId._id || project.clientId,
        projectName: project.projectName || "",
        projectDescription: project.projectDetails?.description || "",
        startDate: project.timeline?.startDate
          ? project.timeline.startDate.split("T")[0]
          : new Date().toISOString().split("T")[0],
        estimatedEndDate: project.timeline?.estimatedEndDate
          ? project.timeline.estimatedEndDate.split("T")[0]
          : "",
        totalAmount: project.paymentStructure?.totalAmount?.toString() || "",
        priority: project.priority || "medium",
        notes: project.notes || "",
      });

      // 🆕 NEW: Set project managers
      if (project.projectManagers) {
        const managerIds = project.projectManagers.map((manager: any) =>
          typeof manager === "string" ? manager : manager._id
        );
        setSelectedProjectManagers(managerIds);
      }

      // Set assigned employees
      if (project.assignedEmployees) {
        const employeeIds = project.assignedEmployees.map((emp: any) =>
          typeof emp === "string" ? emp : emp._id
        );
        setSelectedEmployees(employeeIds);
      }

      // Set payment structure - store initial milestones separately
      if (project.paymentStructure) {
        setPaymentStructure({
          totalAmount: project.paymentStructure.totalAmount,
          currency: project.paymentStructure.currency || "INR",
          milestones: project.paymentStructure.milestones.map(
            (m: any, index: number) => ({
              id: (index + 1).toString(),
              name: m.name,
              percentage: m.percentage,
            })
          ),
        });
      }
    } catch (error: any) {
      console.error("Error fetching project data:", error);
      setError(
        error.response?.data?.message ||
          "Failed to fetch project data. Please try again."
      );
    } finally {
      setIsFetchingData(false);
    }
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setError("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      if (!paymentStructure) {
        setError("Please add at least one milestone");
        setIsLoading(false);
        return;
      }

      const totalPercentage = paymentStructure.milestones.reduce(
        (sum, m) => sum + m.percentage,
        0
      );

      if (totalPercentage !== 100) {
        setError(
          `Total milestone percentage must equal 100%. Current total: ${totalPercentage}%`
        );
        setIsLoading(false);
        return;
      }

      // Prepare the data for submission
      const submitData = {
        clientId: formData.clientId,
        projectName: formData.projectName,
        projectDetails: {
          description: formData.projectDescription,
        },
        timeline: {
          startDate: formData.startDate,
          estimatedEndDate: formData.estimatedEndDate,
        },
        paymentStructure: {
          totalAmount: parseFloat(formData.totalAmount),
          currency: paymentStructure.currency,
          milestones: paymentStructure.milestones.map((milestone) => ({
            name: milestone.name,
            percentage: milestone.percentage,
            // Don't send the 'id' field to backend
          })),
        },
        projectManagers: selectedProjectManagers, // 🆕 NEW
        assignedEmployees: selectedEmployees,
        priority: formData.priority,
        notes: formData.notes,
      };

      const token = localStorage.getItem("authToken");
      const url = isEditMode
        ? `${process.env.REACT_APP_BACKEND_URL}api/projects/${id}`
        : `${process.env.REACT_APP_BACKEND_URL}api/projects`;
      const method = isEditMode ? "put" : "post";

      const response = await axios[method](url, submitData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.data.success) {
        navigate("/projects", {
          state: {
            message: isEditMode
              ? "Project updated successfully!"
              : "Project created successfully!",
          },
        });
      } else {
        setError(response.data.message || "Something went wrong");
      }
    } catch (error: any) {
      console.error("Error submitting project:", error);
      setError(
        error.response?.data?.message ||
          `Failed to ${
            isEditMode ? "update" : "create"
          } project. Please try again.`
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetchingData) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="text-center space-y-4">
          <Spinner
            variant="ellipsis"
            size={60}
            className="text-primary mx-auto"
          />
          <p className="text-lg font-medium">Loading project data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-6 p-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/projects")}
              className="h-8 w-8"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-3xl font-bold tracking-tight">
              {isEditMode ? "Edit Project" : "Add New Project"}
            </h1>
          </div>
          <p className="text-muted-foreground ml-11">
            {isEditMode
              ? "Update project details and settings"
              : "Create a new project with client details and payment structure"}
          </p>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-lg flex items-center gap-2">
              <FolderOpen className="h-5 w-5" />
              Project Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="clientId" className="text-sm font-medium">
                  Client <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={formData.clientId}
                  onValueChange={(value) =>
                    handleInputChange("clientId", value)
                  }
                  disabled={isLoading}
                >
                  <SelectTrigger className="h-10">
                    <SelectValue placeholder="Select a client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client._id} value={client._id}>
                        <div className="flex flex-col">
                          <span className="font-medium">
                            {client.clientName}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="projectName" className="text-sm font-medium">
                  Project Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="projectName"
                  value={formData.projectName}
                  onChange={(e) =>
                    handleInputChange("projectName", e.target.value)
                  }
                  disabled={isLoading}
                  placeholder="Enter project name"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="projectDescription"
                className="text-sm font-medium"
              >
                Description <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="projectDescription"
                value={formData.projectDescription}
                onChange={(e) =>
                  handleInputChange("projectDescription", e.target.value)
                }
                disabled={isLoading}
                placeholder="Describe the project scope and objectives"
                rows={3}
                required
              />
            </div>
          </CardContent>
        </Card>

        {/* 🆕 NEW: Project Managers Section */}
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-lg flex items-center gap-2">
              <UserCog className="h-5 w-5" />
              Project Managers
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isFetchingEmployees ? (
              <div className="flex items-center justify-center py-8">
                <Spinner
                  variant="ellipsis"
                  size={40}
                  className="text-primary"
                />
              </div>
            ) : activeEmployees.length === 0 ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                <div className="flex items-center gap-2">
                  <Info className="h-4 w-4 text-amber-600" />
                  <p className="text-sm text-amber-700">
                    No active employees available. Only active employees can be
                    assigned as project managers.
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {/* <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                  <div className="flex items-center gap-2">
                    <Info className="h-4 w-4 text-blue-600" />
                    <p className="text-sm text-blue-700">
                      Select one or more project managers. Project managers can
                      be assigned to multiple projects.
                    </p>
                  </div>
                </div> */}

                <div className="space-y-2">
                  <Label>Select Project Managers (Optional)</Label>
                  <div className="grid gap-2 max-h-80 overflow-y-auto border rounded-md p-3">
                    {activeEmployees.map((employee) => (
                      <div
                        key={employee._id}
                        className="flex items-start space-x-3 p-2 hover:bg-accent/50 rounded-md transition-colors"
                      >
                        <input
                          type="checkbox"
                          id={`manager-${employee._id}`}
                          checked={selectedProjectManagers.includes(
                            employee._id
                          )}
                          onChange={() =>
                            handleProjectManagerSelection(employee._id)
                          }
                          className="h-4 w-4 rounded border-gray-300 mt-1"
                          disabled={isLoading}
                        />
                        <label
                          htmlFor={`manager-${employee._id}`}
                          className="flex-1 cursor-pointer"
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">
                              {employee.fullName}
                            </span>
                            <span
                              className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold ${
                                employee.employeeType === "Intern"
                                  ? "bg-blue-100 text-blue-800 border-blue-200"
                                  : employee.employeeType === "Part-Time"
                                  ? "bg-purple-100 text-purple-800 border-purple-200"
                                  : "bg-green-100 text-green-800 border-green-200"
                              }`}
                            >
                              {employee.employeeType}
                            </span>
                            <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold bg-green-100 text-green-800 border-green-200">
                              Active
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {employee.email}
                            {employee.designation &&
                              ` • ${employee.designation}`}
                          </p>
                        </label>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    Selected: {selectedProjectManagers.length} project
                    manager(s)
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Team Members Section */}
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="h-5 w-5" />
              Team Members
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isFetchingEmployees ? (
              <div className="flex items-center justify-center py-8">
                <Spinner
                  variant="ellipsis"
                  size={40}
                  className="text-primary"
                />
              </div>
            ) : activeEmployees.length === 0 ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                <div className="flex items-center gap-2">
                  <Info className="h-4 w-4 text-amber-600" />
                  <p className="text-sm text-amber-700">
                    No active employees available. Only active employees can be
                    assigned to projects.
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {/* <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                  <div className="flex items-center gap-2">
                    <Info className="h-4 w-4 text-blue-600" />
                    <p className="text-sm text-blue-700">
                      Select team members for this project. Employees can be
                      assigned to multiple projects.
                    </p>
                  </div>
                </div> */}

                <div className="space-y-2">
                  <Label>Select Team Members (Optional)</Label>
                  <div className="grid gap-2 max-h-80 overflow-y-auto border rounded-md p-3">
                    {activeEmployees.map((employee) => (
                      <div
                        key={employee._id}
                        className="flex items-start space-x-3 p-2 hover:bg-accent/50 rounded-md transition-colors"
                      >
                        <input
                          type="checkbox"
                          id={`employee-${employee._id}`}
                          checked={selectedEmployees.includes(employee._id)}
                          onChange={() => handleEmployeeSelection(employee._id)}
                          className="h-4 w-4 rounded border-gray-300 mt-1"
                          disabled={isLoading}
                        />
                        <label
                          htmlFor={`employee-${employee._id}`}
                          className="flex-1 cursor-pointer"
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">
                              {employee.fullName}
                            </span>
                            <span
                              className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold ${
                                employee.employeeType === "Intern"
                                  ? "bg-blue-100 text-blue-800 border-blue-200"
                                  : employee.employeeType === "Part-Time"
                                  ? "bg-purple-100 text-purple-800 border-purple-200"
                                  : "bg-green-100 text-green-800 border-green-200"
                              }`}
                            >
                              {employee.employeeType}
                            </span>
                            <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold bg-green-100 text-green-800 border-green-200">
                              Active
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {employee.email}
                            {employee.designation &&
                              ` • ${employee.designation}`}
                          </p>
                        </label>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    Selected: {selectedEmployees.length} team member(s)
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Timeline */}
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Timeline
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="startDate" className="text-sm font-medium">
                  Start Date <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="startDate"
                  type="date"
                  value={formData.startDate}
                  onChange={(e) =>
                    handleInputChange("startDate", e.target.value)
                  }
                  disabled={isLoading}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="estimatedEndDate"
                  className="text-sm font-medium"
                >
                  Estimated End Date <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="estimatedEndDate"
                  type="date"
                  value={formData.estimatedEndDate}
                  onChange={(e) =>
                    handleInputChange("estimatedEndDate", e.target.value)
                  }
                  disabled={isLoading}
                  min={formData.startDate}
                  required
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Payment Structure */}
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-lg flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Payment Structure
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="totalAmount" className="text-sm font-medium">
                  Total Amount <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="totalAmount"
                  type="number"
                  value={formData.totalAmount}
                  onChange={(e) =>
                    handleInputChange("totalAmount", e.target.value)
                  }
                  disabled={isLoading}
                  placeholder="Enter total project amount"
                  min="0"
                  step="0.01"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="priority" className="text-sm font-medium">
                  Priority
                </Label>
                <Select
                  value={formData.priority}
                  onValueChange={(value) =>
                    handleInputChange("priority", value)
                  }
                  disabled={isLoading}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {formData.totalAmount && parseFloat(formData.totalAmount) > 0 && (
              <SimplePaymentStructure
                totalAmount={parseFloat(formData.totalAmount)}
                onPaymentStructureChange={setPaymentStructure}
                initialMilestones={paymentStructure?.milestones}
                currency={paymentStructure?.currency || "INR"}
              />
            )}
          </CardContent>
        </Card>

        {/* Additional Notes */}
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-lg">Additional Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="notes" className="text-sm font-medium">
                Notes (Optional)
              </Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => handleInputChange("notes", e.target.value)}
                disabled={isLoading}
                placeholder="Any additional notes or requirements..."
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        {/* Submit Buttons */}
        <div className="flex gap-3 pt-4 justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/projects")}
            disabled={isLoading}
            className="flex-1 sm:flex-none"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={
              isLoading ||
              !formData.clientId ||
              !formData.projectName ||
              !formData.totalAmount ||
              !paymentStructure ||
              paymentStructure.milestones.reduce(
                (sum, m) => sum + m.percentage,
                0
              ) !== 100
            }
            className="flex-1 sm:flex-none min-w-[120px]"
          >
            {isLoading ? (
              <>
                <Spinner
                  variant="ellipsis"
                  size={40}
                  className="text-primary"
                />
                {isEditMode ? "Updating..." : "Creating..."}
              </>
            ) : (
              <>{isEditMode ? "Update Project" : "Create Project"}</>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
